/**
 * What every New sheet is made of — `docs/activity.md` §9, Boards 11, 16 E,
 * 19 C, 20 A.
 *
 * **The shell** is the app's flyout as these boards draw it: 480 for a text,
 * 620 for the rest, from the right over a scrim; a 36 px kind tile (10 px
 * radius, the kind's tint and line) beside a 16/700 title and a 12 px muted
 * line; the fields; a footer on `surface-alt` carrying what will happen on the
 * left and **Cancel · <primary>** on the right. On a phone it is the whole
 * screen with *Cancel · <title> · Create* in a 52 px bar (Board 16 E). Closing
 * with something typed asks first, Escape belongs to the top-most layer, and
 * ⌘↵ presses the primary — `components/sheet.tsx`'s three behaviours, kept.
 *
 * **The fields** are the boards' own: an 11.5 px muted label with the 5 px
 * green dot or *optional* at the right, a 36 px box (46 on a phone), a hint
 * at 11 px faint. **A picker is never a native select**: a box that opens a list
 * under it on a desktop and a bottom sheet of 52 px rows on a phone (§10.2).
 */
import {
  useCallback, useEffect, useRef, useState, type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { ConfirmDialog, Popover, escapeIsFor, watchOverlayKeys } from '../../components/overlays.js'
import { PanelSheet } from '../../components/panel-sheet.js'
import { cn } from '../../lib/cn.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import { BackLayer, useBackLayer } from '../use-activity-history.js'

// ---------------------------------------------------------------------------
// Kind tiles
// ---------------------------------------------------------------------------

export type TileLook = 'text' | 'email' | 'task' | 'meeting' | 'visit' | 'note' | 'voice' | 'request'

const TILE_LOOK: Record<TileLook, { glyph: GlyphName; className: string }> = {
  text: { glyph: 'bubble', className: 'border-teal-band-line bg-teal-band-bg text-teal-band-text' },
  email: { glyph: 'envelope', className: 'border-info-line bg-info-bg text-info' },
  task: { glyph: 'check_square', className: 'border-amber-band-line bg-amber-band-bg text-amber-band-text' },
  meeting: { glyph: 'people', className: 'border-info-line bg-info-bg text-info' },
  /* A site visit wears its kind's green (*Site visit · Meeting* picks the kind and its colour). */
  visit: { glyph: 'pin', className: 'border-accent-line bg-accent-soft text-accent-dark' },
  note: { glyph: 'pencil', className: 'border-line bg-line-soft text-text-muted' },
  voice: { glyph: 'mic', className: 'border-line bg-line-soft text-text-muted' },
  request: { glyph: 'plus', className: 'border-accent-line bg-accent-soft text-accent-dark' },
}

/** A kind's tile: 36 px in a sheet's head (radius 10), 38 px on the phone's New sheet (radius 11). */
export function KindTile({ look, size = 36 }: { look: TileLook; size?: 36 | 38 }) {
  const tile = TILE_LOOK[look]
  return (
    <span aria-hidden
      className={cn('inline-flex shrink-0 items-center justify-center border', tile.className,
        size === 38 ? 'h-[38px] w-[38px] rounded-[11px]' : 'h-9 w-9 rounded-[10px]')}>
      <Glyph name={tile.glyph} size={size === 38 ? 19 : 18} />
    </span>
  )
}

// ---------------------------------------------------------------------------
// The shell
// ---------------------------------------------------------------------------

export interface NewShellProps {
  title: string
  sub: string
  look: TileLook
  width: 480 | 620
  phone: boolean
  /** The footer's left — *Shows on Renee, the residence and J-3318*. */
  note: ReactNode
  /** *Send*, *Create task*, *Create meeting*, *Save note*, *Create list*. */
  primary: string
  /** The phone bar's right-hand word — *Create*, or *Send* for a text and an email (§9.8). */
  phoneAction: string
  onPrimary: () => void
  busy?: boolean
  dirty: boolean
  onClose: () => void
  /** A refusal from the server, under the fields. */
  problem?: string | null
  children: ReactNode
}

/** The layer New's sheets sit on, and the one their pickers open on. */
export const SHEET_LAYER = 50
export const PICKER_LAYER = 70

export function NewShell({
  title, sub, look, width, phone, note, primary, phoneAction, onPrimary, busy = false, dirty, onClose,
  problem, children,
}: NewShellProps) {
  const panel = useRef<HTMLDivElement>(null)
  const [asking, setAsking] = useState(false)
  const layer = phone ? 60 : SHEET_LAYER

  /** Closes it — or, with something typed, asks first and says so (`false`). */
  const attemptClose = useCallback((): boolean => {
    if (dirty) { setAsking(true); return false }
    onClose()
    return true
  }, [dirty, onClose])
  /* Back is a close like Cancel: with changes it asks, and the sheet stays until it is answered (Aaron, Oct 26). */
  useBackLayer(attemptClose)

  useEffect(() => {
    watchOverlayKeys()
    const onKey = (event: KeyboardEvent) => {
      const node = panel.current
      if (!node || !(event.target instanceof Node) || !node.contains(event.target)) {
        if (event.key !== 'Escape' || !escapeIsFor(layer)) return
      }
      if (event.key === 'Escape') {
        if (!escapeIsFor(layer)) return
        event.stopPropagation()
        attemptClose()
        return
      }
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        if (!busy) onPrimary()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [attemptClose, onPrimary, busy, layer])

  const dialog = asking && (
    <ConfirmDialog
      title="Close without saving?"
      body={`${title} has changes that will be lost. Keep editing, or throw them away.`}
      confirmLabel={primary}
      cancelLabel="Keep editing"
      layer={layer + 5}
      tone="default"
      extra={(
        <button type="button" onClick={() => { setAsking(false); onClose() }}
          className="mr-auto text-body font-semibold text-danger hover:underline">
          Discard changes
        </button>
      )}
      onConfirm={() => { setAsking(false); onPrimary() }}
      onCancel={() => setAsking(false)}
    />
  )

  if (phone) {
    return createPortal(
      <>
        <div ref={panel} role="dialog" aria-modal="true" aria-label={title} data-overlay-layer={layer}
          style={{ zIndex: layer }} className="fixed inset-0 flex flex-col bg-surface">
          <header className="flex h-[52px] shrink-0 items-center border-b border-line bg-surface px-3.5">
            <button type="button" onClick={attemptClose} className="w-[70px] text-left text-[15px] text-accent">
              Cancel
            </button>
            <b className="min-w-0 flex-1 truncate text-center text-[15.5px]">{title}</b>
            <button type="button" disabled={busy} onClick={onPrimary}
              className="w-[70px] text-right text-[15px] font-bold text-accent disabled:opacity-50">
              {phoneAction}
            </button>
          </header>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-surface p-4 pb-[calc(24px+env(safe-area-inset-bottom))] [&>*]:shrink-0">
            {children}
            {problem && <p role="alert" className="mt-3 text-[13px] text-danger">{problem}</p>}
          </div>
        </div>
        {dialog}
      </>,
      document.body,
    )
  }

  return createPortal(
    <>
      <div style={{ zIndex: layer }} data-overlay-layer={layer}
        className="fixed inset-0 flex justify-end bg-[rgba(29,29,26,.18)]"
        onMouseDown={(event) => { if (event.target === event.currentTarget) attemptClose() }}>
        <div ref={panel} role="dialog" aria-modal="true" aria-label={title} style={{ width, maxWidth: '100%' }}
          className="flex h-full flex-col border-l border-line bg-surface shadow-slideover">
          <header className="flex shrink-0 items-center gap-3 border-b border-line-faint px-5 py-4">
            <KindTile look={look} />
            <div className="flex min-w-0 flex-col">
              <b className="truncate text-[16px]">{title}</b>
              <span className="truncate text-[12px] text-text-muted">{sub}</span>
            </div>
            <span className="flex-1" />
            <button type="button" aria-label="Close" onClick={attemptClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-text-muted hover:bg-hover">
              <Glyph name="close" size={18} />
            </button>
          </header>
          {/* Rows keep their height when the sheet is taller than the window: it scrolls, nothing squeezes. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-[18px] [&>*]:shrink-0">
            {children}
            {problem && <p role="alert" className="mt-3 text-[12.5px] text-danger">{problem}</p>}
          </div>
          <footer className="flex shrink-0 items-center gap-2.5 border-t border-line bg-surface-alt px-5 py-3.5">
            <div className="min-w-0 flex-1 text-[12px] leading-[1.4] text-text-muted">{note}</div>
            <button type="button" onClick={attemptClose}
              className="inline-flex h-9 items-center justify-center whitespace-nowrap rounded-lg border border-line bg-surface px-3.5 text-[13px] font-semibold text-text hover:bg-hover">
              Cancel
            </button>
            <button type="button" disabled={busy} onClick={onPrimary}
              className="inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-accent bg-accent px-3.5 text-[13px] font-semibold text-white hover:bg-accent-dark disabled:opacity-60">
              <Glyph name="check" size={15} stroke={2.2} />{primary}
            </button>
          </footer>
        </div>
      </div>
      {dialog}
    </>,
    document.body,
  )
}

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

/** 12 px between rows, the boards' rhythm (10 in the email sheet, which is denser). */
export function Gap({ size = 12 }: { size?: 8 | 10 | 12 }) {
  return <div aria-hidden style={{ height: size }} />
}

export function FieldLabel({ label, required, optional, htmlFor, id }: {
  label: string; required?: boolean; optional?: boolean; htmlFor?: string; id?: string
}) {
  return (
    <div className="mb-[5px] flex items-center gap-1 text-[11.5px] font-medium text-text-muted">
      {htmlFor ? <label id={id} htmlFor={htmlFor}>{label}</label> : <span id={id}>{label}</span>}
      {required && <i aria-hidden className="inline-block h-[5px] w-[5px] rounded-full bg-accent" />}
      {optional && <span className="ml-auto text-text-faint">optional</span>}
    </div>
  )
}

export function Hint({ children, phone }: { children: ReactNode; phone?: boolean }) {
  return (
    <div className={cn('leading-[1.4] text-text-faint', phone ? 'mt-[5px] text-[12px]' : 'mt-1 text-[11px]')}>
      {children}
    </div>
  )
}

export function Field({
  label, required, optional, hint, phone, htmlFor, children, className,
}: {
  label: string; required?: boolean; optional?: boolean; hint?: ReactNode; phone?: boolean; htmlFor?: string
  children: ReactNode; className?: string
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <FieldLabel label={label} required={required} optional={optional} htmlFor={htmlFor} />
      {children}
      {hint && <Hint phone={phone}>{hint}</Hint>}
    </div>
  )
}

/** Two fields side by side: 14 px apart on a desktop (Board 11), 10 on a phone (Board 16 E). */
export function Pair({ children, phone }: { children: ReactNode; phone?: boolean }) {
  return <div className={cn('grid grid-cols-2', phone ? 'gap-2.5' : 'gap-x-3.5 gap-y-3')}>{children}</div>
}

/** The box every one-line control is drawn in. */
export function boxClass(phone: boolean, extra?: string): string {
  return cn('flex w-full min-w-0 items-center border border-line bg-surface text-text',
    phone ? 'h-[46px] gap-2 rounded-[10px] px-3 text-[15px]' : 'h-9 gap-1.5 rounded-lg px-[11px] text-[13px]', extra)
}

/** The focus the boards draw on the field being typed in: accent line, a 3 px ring. */
export const FOCUS = 'focus-within:border-accent focus-within:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]'

export function TextBox({
  value, onChange, placeholder, phone, autoFocus, id, bold, label, onEnter,
}: {
  value: string; onChange: (value: string) => void; placeholder?: string; phone: boolean; autoFocus?: boolean
  id?: string; bold?: boolean; label?: string; onEnter?: () => void
}) {
  return (
    <span className={boxClass(phone, FOCUS)}>
      <input id={id} aria-label={label} value={value} autoFocus={autoFocus} placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter' && onEnter) { event.preventDefault(); onEnter() } }}
        className={cn('min-w-0 flex-1 bg-transparent outline-none focus-visible:shadow-none placeholder:text-text-faint',
          bold && 'font-semibold')} />
    </span>
  )
}

// ---------------------------------------------------------------------------
// Pickers — a list under the box, or a bottom sheet of 52 px rows
// ---------------------------------------------------------------------------

export interface PickOption<T extends string> {
  value: T
  label: string
  /** A face, a pin, a calendar glyph before the label. */
  lead?: ReactNode
  /** Quiet words after it — *Camp Hill, PA*, *Tuesdays 8:00 AM*. */
  after?: string
  /** A muted line under the label in the list. */
  sub?: string
  /** Shown, not pickable — *Attach a job to book a site visit*. */
  disabled?: boolean
}

export function MenuField<T extends string>({
  label, value, options, onChange, phone, placeholder, width, display, boldValue, id,
}: {
  label: string
  value: T | null
  options: readonly PickOption<T>[]
  onChange: (value: T) => void
  phone: boolean
  placeholder?: string
  /** A fixed box width — the time (92 / 86) and the length (56) beside a date. */
  width?: number
  /** What the box says, when it is not the chosen option's label. */
  display?: ReactNode
  boldValue?: boolean
  id?: string
}) {
  const [open, setOpen] = useState(false)
  const button = useRef<HTMLButtonElement>(null)
  const chosen = options.find((option) => option.value === value) ?? null
  const shown = display ?? (chosen ? (
    <>
      {chosen.lead}
      <span className={cn('truncate', boldValue && 'font-bold')}>{chosen.label}</span>
      {chosen.after && <span className="truncate text-text-muted">{chosen.after}</span>}
    </>
  ) : <span className="truncate text-text-faint">{placeholder ?? 'Choose…'}</span>)
  const pick = (next: PickOption<T>) => {
    if (next.disabled) return
    onChange(next.value)
    setOpen(false)
  }

  return (
    <>
      <button ref={button} id={id} type="button" aria-label={label} aria-haspopup="listbox" aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        style={width ? { width, flex: `0 0 ${width}px` } : undefined}
        className={boxClass(phone, cn('text-left', open && 'border-accent-line'))}>
        <span className="flex min-w-0 flex-1 items-center gap-2">{shown}</span>
        {/* A phone's picker box is plain (Board 16 E); a desktop's carries its chevron (Board 11). */}
        {!width && !phone && (
          <span className="shrink-0 text-text-muted"><Glyph name="chevronDown" size={14} /></span>
        )}
      </button>
      {/* A phone's picker is a sheet, and back closes it before the sheet under it (Aaron, Oct 26). */}
      {phone && open && <BackLayer onBack={() => setOpen(false)} />}
      {phone ? (
        <PanelSheet open={open} onClose={() => setOpen(false)} title={label} layer={PICKER_LAYER}>
          <div role="listbox" aria-label={label} className="flex flex-col">
            {options.map((option) => (
              <button key={option.value} type="button" role="option" aria-selected={option.value === value}
                aria-disabled={option.disabled || undefined} onClick={() => pick(option)}
                className={cn('flex min-h-[52px] w-full items-center gap-3 border-b border-line-faint px-4 text-left',
                  option.value === value ? 'bg-green-band-bg' : 'bg-surface', option.disabled && 'opacity-55')}>
                {option.lead}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[15px] font-medium text-text">{option.label}</span>
                  {(option.sub ?? option.after) && (
                    <span className="truncate text-[12.5px] text-text-muted">{option.sub ?? option.after}</span>
                  )}
                </span>
                <span className={option.value === value ? 'text-accent' : 'text-transparent'}>
                  <Glyph name="check" size={16} />
                </span>
              </button>
            ))}
          </div>
        </PanelSheet>
      ) : (
        <Popover open={open} onClose={() => setOpen(false)} anchor={button.current} label={label}
          width={Math.max(width ?? 0, button.current?.offsetWidth ?? 220, 200)} layer={PICKER_LAYER} maxHeight={320}
          className="py-1">
          <div role="listbox" aria-label={label} className="flex flex-col overflow-y-auto">
            {options.map((option) => (
              <button key={option.value} type="button" role="option" aria-selected={option.value === value}
                aria-disabled={option.disabled || undefined} onClick={() => pick(option)}
                className={cn('flex w-full items-center gap-2 px-3 py-[7px] text-left text-[13px]',
                  option.value === value ? 'bg-green-band-bg font-semibold' : 'hover:bg-hover',
                  option.disabled && 'cursor-default opacity-55 hover:bg-transparent')}>
                {option.lead}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate">{option.label}</span>
                    {option.after && <span className="truncate font-normal text-text-muted">{option.after}</span>}
                  </span>
                  {option.sub && <span className="truncate text-[11.5px] font-normal text-text-muted">{option.sub}</span>}
                </span>
              </button>
            ))}
          </div>
        </Popover>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Small controls
// ---------------------------------------------------------------------------

/** The grey track of two or more choices (Board 11 F's *Site visit · Meeting*, G's *Office · Office and crew*). */
export function Segments<T extends string>({
  options, value, onChange, label, height = 24, fill = false,
}: {
  options: readonly { value: T; label: string; title?: string }[]
  value: T
  onChange: (value: T) => void
  label: string
  height?: 24 | 28 | 36
  fill?: boolean
}) {
  return (
    <span role="radiogroup" aria-label={label}
      className={cn('inline-flex shrink-0 rounded-[10px] bg-line-soft p-[3px]', fill && 'w-full')}>
      {options.map((option) => (
        <button key={option.value} type="button" role="radio" aria-checked={option.value === value}
          title={option.title} onClick={() => onChange(option.value)} style={{ height }}
          className={cn('inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 text-[12.5px] font-semibold',
            fill && 'flex-1',
            option.value === value ? 'bg-surface text-text shadow-[0_1px_2px_rgba(20,20,10,.12)]' : 'text-text-muted hover:text-text')}>
          {option.label}
        </button>
      ))}
    </span>
  )
}

/** A 30 × 18 switch (Board 11 F, G). */
export function SmallSwitch({ on }: { on: boolean }) {
  return (
    <span aria-hidden className={cn('inline-flex h-[18px] w-[30px] shrink-0 items-center rounded-full p-0.5',
      on ? 'justify-end bg-accent' : 'justify-start bg-line-strong')}>
      <i className="block h-3.5 w-3.5 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,.25)]" />
    </span>
  )
}

/** A 28 px square tool under a message box — camera, paperclip. */
export function ToolSquare({ glyph, label, onClick }: { glyph: GlyphName; label: string; onClick?: () => void }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick}
      className="inline-flex h-7 w-7 items-center justify-center rounded-[7px] border border-line text-text-muted hover:bg-hover">
      <Glyph name={glyph} size={15} />
    </button>
  )
}

/** A 28 px worded tool — *From the job*. */
export function ToolChip({ glyph, children, onClick }: { glyph: GlyphName; children: ReactNode; onClick?: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="inline-flex h-7 items-center gap-[5px] rounded-[7px] border border-line bg-surface px-[9px] text-[12px] font-semibold text-text-body hover:bg-hover">
      <Glyph name={glyph} size={14} />{children}
    </button>
  )
}

/**
 * A message or a note: the words, and a row of tools under a hairline
 * (Board 11 B, D, E, G). On a phone a plain box with the mic inside it
 * (Board 16 E's *Notes*).
 */
export function WordsBox({
  value, onChange, placeholder, minHeight, tools, phone, label, id, phoneTool,
}: {
  value: string; onChange: (value: string) => void; placeholder?: string; minHeight: number
  tools?: ReactNode; phone: boolean; label: string; id?: string
  /** The phone's one tool, inside the box at the right — the mic. */
  phoneTool?: ReactNode
}) {
  if (phone) {
    return (
      <div className={cn('flex items-start gap-2 rounded-[10px] border border-line px-3 py-2.5', FOCUS)}
        /* Board 16 E: 70 inside the box's padding and border — 92 drawn. */
        style={{ minHeight: Math.max(92, minHeight) }}>
        <textarea id={id} aria-label={label} value={value} placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)} rows={2}
          className="min-w-0 flex-1 resize-none bg-transparent text-[14.5px] leading-[1.45] outline-none focus-visible:shadow-none placeholder:text-text-faint" />
        {phoneTool}
      </div>
    )
  }
  return (
    <div className={cn('overflow-hidden rounded-[10px] border border-line bg-surface', FOCUS)}>
      <textarea id={id} aria-label={label} value={value} placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        /* Board 11's heights are the words' own, inside 10 px of padding above and below. */
        style={{ minHeight: minHeight + 20 }}
        className="block w-full resize-none bg-transparent px-3 py-2.5 text-[13px] leading-[1.5] outline-none focus-visible:shadow-none placeholder:text-text-faint" />
      {tools && (
        <div className="flex items-center gap-1.5 border-t border-line-faint px-2 py-1.5">{tools}</div>
      )}
    </div>
  )
}
